import { ModelDropdown } from "@/components/molecules/ModelDropdown/ModelDropdown";
import { FeatureHubGuard } from "@/featureFlags";
import type { LayerKind } from "@/layers/utility/createLayerObject";
import { InitDateField } from "@/overlay/components/LayerStackWindow/InitDateField";
import { ColorPickerInput } from "@/overlay/components/LayerStackWindow/LayerFormElements";
import { WeatherParameterForm } from "@/overlay/components/WeatherParameterForm";
import { FormField } from "@/overlay/components/form-field";
import {
  addPlotSeriesThreshold,
  removePlotSeriesThreshold,
  setPlotSerieProps,
  setPlotSeriesThresholds,
  setPlotYaxisOption,
  setThresholdsOrder,
} from "@/reducer/PlotsState";
import {
  type PlotSeriesType,
  type PlotThreshold,
  SeriesDashStyles,
  SeriesTypes,
  seriesDashStyles,
  seriesTypes,
  yAxisList,
} from "@/reducer/client-models";
import { hasCalibratedAvailable } from "@/utility/calibratedAvailabilityMap";
import { EnsSelectStat, band100p, constructStringFromMultiSelectEnsembles } from "@/utility/ensemble";
import { getDefaultModelName } from "@/utility/layer";
import {
  searchEnginesWeatherParams,
  stationModels_TEMPORAL,
  stationMosModel_TEMPORAL,
  stationObsModel_TEMPORAL,
  useSearchEngineReady,
  useWeatherParamModels,
  winterdienstStationModels_TEMPORAL,
} from "@/weather-parameters";
import { t } from "@lingui/core/macro";
import type { ModelSchema } from "@mm/api-layers.meteomatics.com";
import Logger from "@mm/logging";
import type { ExactMatchResult, HasLayerIdxNarrowing, PartialWeatherParameter } from "@mm/weather-parameter-utils";
import { autofill, parameterToString } from "@mm/weather-parameter-utils";
import { useFlag } from "@openfeature/react-sdk";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import type { DraggableItemIdentifier } from "../../CommonParts/SortableList";
import { GroupedEnsembleMembers } from "../../Ensamble/GroupedEnsembleMember";
import { ThresholdPanel } from "./ThresholdPanel";

const logger = Logger.fromFilename(__filename);

type PlotConfigType = {
  plotId: number;
  plotSerie: PlotSeriesType;
};

const getLayerDescription = (seriesType: string): LayerKind | undefined => {
  if (seriesType === "windbarb") {
    return "BarbsLayerDescription";
  }

  if (seriesType === "weathersymbol") {
    return "SymbolLayerDescription";
  }
};

function getSearchEngineByModel(modelId: string) {
  if (modelId === stationMosModel_TEMPORAL.identifier) {
    return searchEnginesWeatherParams.stationMos;
  }
  if (modelId === stationObsModel_TEMPORAL.identifier) {
    return searchEnginesWeatherParams.stationObs;
  }
  return searchEnginesWeatherParams.standard;
}

export const OptionSeparatorModel: ModelSchema = {
  bounding_box: {
    resolution_lng: 0,
    resolution_lat: 0,
    south: 0,
    west: 0,
    north: 0,
    east: 0,
  },
  description_de: "",
  description_en: "",
  identifier: "---",
  name: "-------",
};

export function PlotSerieOptions({ plotId, plotSerie }: PlotConfigType) {
  const dispatch = useDispatch();
  const { model: modelId, parameter_unit } = plotSerie;
  const weatherModels = useWeatherParamModels();
  const [currentParam, setCurrentParam] = useState<PartialWeatherParameter | null>(null);
  const [currentParamStandardParam, setCurrentParamStandardParam] = useState<PartialWeatherParameter | null>(null);
  const searchEngineReady = useSearchEngineReady();
  const searchEngine = getSearchEngineByModel(plotSerie.model);
  const [models, setModels] = useState<ModelSchema[] | null>(null);
  const [ensModels, setEnsModels] = useState<ModelSchema[] | null>(null);
  const [selectedModelSchema, setSelectedModelSchema] = useState<ModelSchema | null>(null);
  const preferredLayerIdxRef = useRef<number | null>(null);
  const isWindBarbOrWeatherSymbol = useMemo(
    () => plotSerie.series_type === "windbarb" || plotSerie.series_type === "weathersymbol",
    [plotSerie.series_type],
  );
  const { value: winterdienstStationsEnabled } = useFlag("winterdienst-stations", false);

  useEffect(() => {
    preferredLayerIdxRef.current = currentParam?.narrowed_selection?.layerIdx ?? null;
  }, [currentParam]);

  useEffect(() => {
    searchEnginesWeatherParams.standard
      .search(parameter_unit)
      .then((response) => {
        if (response.results?.regular?.[0]) {
          const result: ExactMatchResult = response.results.regular[0];
          const preferredLayer =
            result.matchingLayers.find((matchingLayer) => matchingLayer.layerIdx === preferredLayerIdxRef.current) ??
            result.matchingLayers[0];
          setCurrentParamStandardParam(
            autofill({
              parameter: result.layerGroup,
              narrowed_selection: {
                layerIdx: preferredLayer.layerIdx,
                // biome-ignore lint/style/noNonNullAssertion: TODO a lot
                fields: preferredLayer.match.groups!,
              },
            }),
          );
        }
      })
      .catch((e) => {
        logger.error(`standard search for term close to ${parameter_unit} errored out.`, e);
      });
  }, [parameter_unit]);

  useEffect(() => {
    searchEngine
      .search(parameter_unit)
      .then((response) => {
        if (response.results?.regular?.[0]) {
          const result: ExactMatchResult = response.results.regular[0];
          const preferredLayer =
            result.matchingLayers.find((matchingLayer) => matchingLayer.layerIdx === preferredLayerIdxRef.current) ??
            result.matchingLayers[0];
          setCurrentParam(
            autofill({
              parameter: result.layerGroup,
              narrowed_selection: {
                layerIdx: preferredLayer.layerIdx,
                // biome-ignore lint/style/noNonNullAssertion: TODO a lot
                fields: preferredLayer.match.groups!,
              },
            }),
          );
        }
      })
      .catch((e) => {
        logger.error(`search for term probably close to ${parameter_unit} errored out.`, e);
      });
  }, [parameter_unit, searchEngine]);

  useEffect(() => {
    if ("parameter_unit" in plotSerie && weatherModels) {
      setModels([
        ...weatherModels.filter((model) =>
          //   filter always by the currentParamStandardParam
          currentParamStandardParam?.parameter.available_models?.includes(model.identifier),
        ),
        OptionSeparatorModel,
        ...stationModels_TEMPORAL,
        ...(winterdienstStationsEnabled ? winterdienstStationModels_TEMPORAL : []),
      ]);
      setEnsModels(weatherModels.filter((model) => model.num_ensemble_members));
      const modelSchema = weatherModels?.find((model) => model.identifier === plotSerie.model);
      setSelectedModelSchema(modelSchema ? modelSchema : null);
    }
  }, [plotSerie, weatherModels, currentParamStandardParam]);

  function changeProps(propsValue: Partial<PlotSeriesType>) {
    let newPropsValue: Partial<PlotSeriesType> = { ...propsValue };

    // check if ens_select there and changePropsValue is with model
    if (
      plotSerie.ens_select &&
      propsValue.model &&
      ensModels != null &&
      !ensModels.some((ensModel) => propsValue.model === ensModel.identifier)
    ) {
      // reset ens_select
      newPropsValue = { ...newPropsValue, ens_select: undefined };
    }

    // model switch ens to other ens model with check if ens_select available in new ens model
    if (
      !plotSerie.ens_select &&
      propsValue.model &&
      ensModels != null &&
      ensModels.some((ensModel) => propsValue.model === ensModel.identifier)
    ) {
      //set to 100% band and Mean
      newPropsValue = {
        ...newPropsValue,
        ens_select: constructStringFromMultiSelectEnsembles(band100p, EnsSelectStat.mean),
      };
    }

    if (newPropsValue && typeof newPropsValue.series_y_axis === "number") {
      dispatch(
        setPlotYaxisOption({
          id: plotId,
          plotSerieYAxisIdx: newPropsValue.series_y_axis,
          yAxis: { visible: true },
        }),
      );
    }

    dispatch(setPlotSerieProps({ id: plotId, plotSerieIdx: plotSerie.index, props: newPropsValue }));
  }

  function update(param: HasLayerIdxNarrowing<PartialWeatherParameter>) {
    if (currentParam && searchEngineReady) {
      setCurrentParam(param);
      const parameter = parameterToString(param);
      // TODO: check if new Parameter has other Models then befor if so change the select of model, models options anyway needs to be updated
      if (parameter && parameter.missingFields.length === 0) {
        changeProps({ parameter_unit: parameter.formatted });
      }
    }
  }

  function updatePlotSeries(param: HasLayerIdxNarrowing<PartialWeatherParameter>) {
    setCurrentParam(param);
    const paramStrFormatted = parameterToString(param);
    if (!paramStrFormatted || paramStrFormatted.missingFields.length > 0) {
      return;
    }

    const model = getDefaultModelName(autofill(param), plotSerie.model);
    dispatch(
      setPlotSerieProps({
        id: plotId,
        plotSerieIdx: plotSerie.index,
        props: { ...plotSerie, model: model, parameter_unit: paramStrFormatted.formatted },
      }),
    );
  }

  const onThresholdAdd = (threshold: PlotThreshold) => {
    dispatch(
      addPlotSeriesThreshold({
        plotId: plotId,
        plotSerieIdx: plotSerie.index,
        threshold: threshold,
      }),
    );
  };

  const onThresholdChange = (thresholdIdx: number, props: Partial<PlotThreshold>) => {
    dispatch(
      setPlotSeriesThresholds({
        plotId: plotId,
        plotSerieIdx: plotSerie.index,
        thresholdIdx: thresholdIdx,
        props: props,
      }),
    );
  };

  const sortedThresholds = plotSerie.thresholds?.concat().sort((a, b) => (a.index < b.index ? -1 : 1));
  function moveThresholdIdx(dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) {
    if (!sortedThresholds) {
      logger.error("Threshold orders were swapped but no data is available");
      return;
    }

    const dragStartThreshold = sortedThresholds[dragStart.index];
    const moveUpBy = dragStart.index - dropTarget.index;

    dispatch(
      setThresholdsOrder({
        plotId: plotId,
        plotSerieIdx: plotSerie.index,
        thresholdIdx: dragStartThreshold.index,
        moveUpBy: moveUpBy,
      }),
    );
  }

  const onThresholdRemove = (thresholdIdx: number) => {
    dispatch(
      removePlotSeriesThreshold({
        plotId: plotId,
        plotSerieIdx: plotSerie.index,
        thresholdIdx: thresholdIdx,
      }),
    );
  };

  if (!searchEngineReady || weatherModels == null || currentParam == null || currentParamStandardParam == null) {
    return <></>;
  }

  return (
    <div>
      {models && (
        <FormField label={t`Source`}>
          <ModelDropdown
            options={models?.map((model: any) => ({ id: model.identifier, title: `${model.name}` })) || []}
            value={modelId}
            onChange={(newModel) => {
              changeProps({ model: newModel });
            }}
            placeholder={"Select a model"}
          />
        </FormField>
      )}
      {currentParam && (
        <WeatherParameterForm
          // pass BarbsLayerDescription to WeatherParameterForm so
          // only working parameter options are shown
          layerKind={getLayerDescription(plotSerie.series_type)}
          // Disable "edit parameter" feature in windbarb layer
          onSubmitParam={!isWindBarbOrWeatherSymbol ? (param) => updatePlotSeries(param) : undefined}
          autofill={true}
          showExactParameter={true}
          parameter={currentParam}
          onChange={update}
        />
      )}
      {selectedModelSchema?.num_ensemble_members && (
        <GroupedEnsembleMembers
          ensSelect={plotSerie.ens_select}
          selectedModelSchema={selectedModelSchema}
          onChange={changeProps}
        />
      )}
      <FeatureHubGuard flag="api_url_suffix">
        <InitDateField
          label={t`API URL suffix`}
          defaultValue={plotSerie.api_url_suffix ?? ""}
          onChange={(apiUrlSuffix) => changeProps({ api_url_suffix: apiUrlSuffix })}
        />
      </FeatureHubGuard>
      {hasCalibratedAvailable(plotSerie.parameter_unit, plotSerie.model) && (
        <FormField label={t`Calibrated`}>
          <input
            type="checkbox"
            checked={plotSerie.calibrated}
            onChange={() => {
              changeProps({ calibrated: !plotSerie.calibrated });
            }}
          />
        </FormField>
      )}
      {/* display color options not on multi member and multi quantiles but for the rest */}
      {!selectedModelSchema?.num_ensemble_members && (
        <ColorPickerInput
          label={t`Color`}
          change={(color) => changeProps({ series_color: color })}
          value={plotSerie.series_color}
        />
      )}
      {!isWindBarbOrWeatherSymbol && (
        <>
          {!selectedModelSchema?.num_ensemble_members && (
            <>
              {/* series type not needed when we have fixed "arearange" type => special case on ensselect plot bands*/}
              <FormField label={t`Series type`}>
                <select
                  value={plotSerie.series_type}
                  onChange={(event) => changeProps({ series_type: event.target.value })}
                >
                  {seriesTypes?.map((seriesType) => (
                    <option key={seriesType} value={seriesType}>
                      {SeriesTypes[seriesType]}
                    </option>
                  ))}
                </select>
                {/* line type not needed when we have "arearange" type => special case on ensselect plot bands*/}
              </FormField>
              <FormField label={t`Line type`}>
                <select
                  value={plotSerie.series_line_type}
                  onChange={(event) => changeProps({ series_line_type: event.target.value })}
                >
                  {seriesDashStyles?.map((seriesDashStyle) => (
                    <option key={seriesDashStyle} value={seriesDashStyle}>
                      {SeriesDashStyles[seriesDashStyle]}
                    </option>
                  ))}
                </select>
              </FormField>
            </>
          )}

          <FormField label={t`Y-Axis`}>
            <select
              value={plotSerie.series_y_axis}
              onChange={(event) => changeProps({ series_y_axis: +event.target.value })}
            >
              {yAxisList?.map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={t`Show Labels`}>
            <input
              type="checkbox"
              checked={plotSerie?.show_series_label}
              onChange={() => {
                changeProps({ show_series_label: !plotSerie.show_series_label });
              }}
            />
          </FormField>
          {plotSerie?.show_series_label && (
            <FormField label={t`Exclude Zeros`}>
              <input
                type="checkbox"
                checked={plotSerie?.exclude_zeros}
                onChange={() => {
                  changeProps({ exclude_zeros: !plotSerie.exclude_zeros });
                }}
              />
            </FormField>
          )}
        </>
      )}
      {!isWindBarbOrWeatherSymbol && (
        <ThresholdPanel
          thresholds={sortedThresholds ?? []}
          onMoveIndex={moveThresholdIdx}
          onThresholdAdd={onThresholdAdd}
          onThresholdChange={onThresholdChange}
          onThresholdRemove={onThresholdRemove}
        />
      )}
    </div>
  );
}
